<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
      <b-card-title class="mb-0">
        테마
      </b-card-title>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="search"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-50"
        />
        검색
      </b-button>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="errorMessage"
        variant="danger"
        show
      >
        {{ errorMessage }}
      </b-alert>

      <div class="d-flex flex-wrap align-items-center mb-1 theme-filter-row">
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">날짜구분</label>
          <b-form-select
            v-model="dateTp"
            :options="dateTpOptions"
            size="sm"
            style="width: 120px;"
          />
        </div>
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">거래소</label>
          <b-form-select
            v-model="stexTp"
            :options="stexTpOptions"
            size="sm"
            style="width: 120px;"
          />
        </div>
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">등락수익구분</label>
          <b-form-select
            v-model="fluPlAmtTp"
            :options="fluPlAmtTpOptions"
            size="sm"
            style="width: 170px;"
          />
        </div>
        <div class="flex-grow-1 mb-50">
          <label class="form-label d-block mb-25">테마명</label>
          <b-input-group>
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-prepend>
            <b-form-input
              v-model="themaNm"
              placeholder="테마명 입력"
              @keyup.enter="search"
            />
          </b-input-group>
        </div>
      </div>

      <b-table
        :items="items"
        :fields="fields"
        responsive
        small
        hover
        show-empty
        empty-text="조회된 테마가 없습니다."
        :busy="loading"
        @row-clicked="openDetailModal"
      >
        <template #cell(fluRt)="row">
          <span :class="rateClass(row.item.fluSig)">{{ formatSigned(row.item.fluRt, '%') }}</span>
        </template>
        <template #cell(dtPrftRt)="row">
          <span :class="rateClassByValue(row.item.dtPrftRt)">{{ formatSigned(row.item.dtPrftRt, '%') }}</span>
        </template>
      </b-table>

      <div class="d-flex justify-content-center mt-2">
        <b-button
          v-if="hasMore"
          variant="outline-primary"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <b-spinner
            v-if="loadingMore"
            small
            class="mr-50"
          />
          더 불러오기
        </b-button>
      </div>
    </b-card-body>

    <b-modal
      id="theme-detail-modal"
      v-model="detailModalVisible"
      size="xl"
      :title="`${selectedTheme.themaNm || '테마'} 구성종목`"
      hide-footer
    >
      <div class="d-flex flex-wrap mb-1">
        <div class="mr-2 mb-50">
          <small class="text-muted d-block">테마코드</small>
          <strong>{{ selectedTheme.themaGrpCd || '-' }}</strong>
        </div>
        <div class="mr-2 mb-50">
          <small class="text-muted d-block">등락률</small>
          <strong :class="rateClass(selectedTheme.fluSig)">{{ formatSigned(selectedTheme.fluRt, '%') }}</strong>
        </div>
        <div class="mr-2 mb-50">
          <small class="text-muted d-block">기간수익률</small>
          <strong :class="rateClassByValue(selectedTheme.dtPrftRt)">{{ formatSigned(selectedTheme.dtPrftRt, '%') }}</strong>
        </div>
      </div>

      <b-alert
        v-if="detailErrorMessage"
        variant="danger"
        show
      >
        {{ detailErrorMessage }}
      </b-alert>

      <div
        v-if="detailLoading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <b-table
        v-else
        :items="detailItems"
        :fields="detailFields"
        responsive
        small
        show-empty
        empty-text="구성종목이 없습니다."
      >
        <template #cell(curPrc)="row">
          {{ formatNumber(row.item.curPrc) }}
        </template>
        <template #cell(predPre)="row">
          {{ formatSigned(row.item.predPre) }}
        </template>
        <template #cell(fluRt)="row">
          <span :class="rateClass(row.item.fluSig)">{{ formatSigned(row.item.fluRt, '%') }}</span>
        </template>
        <template #cell(accTrdeQty)="row">
          {{ formatNumber(row.item.accTrdeQty) }}
        </template>
        <template #cell(selBid)="row">
          {{ formatNumber(row.item.selBid) }}
        </template>
        <template #cell(selReq)="row">
          {{ formatNumber(row.item.selReq) }}
        </template>
        <template #cell(buyBid)="row">
          {{ formatNumber(row.item.buyBid) }}
        </template>
        <template #cell(buyReq)="row">
          {{ formatNumber(row.item.buyReq) }}
        </template>
        <template #cell(dtPrftRtN)="row">
          <span :class="rateClassByValue(row.item.dtPrftRtN)">{{ formatSigned(row.item.dtPrftRtN, '%') }}</span>
        </template>
      </b-table>
    </b-modal>
  </b-card>
</template>

<script>
import axios from '@axios'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupPrepend,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupPrepend,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      detailLoading: false,
      detailModalVisible: false,
      dateTp: '1',
      stexTp: '1',
      fluPlAmtTp: '3',
      themaNm: '',
      errorMessage: '',
      detailErrorMessage: '',
      items: [],
      detailItems: [],
      hasMore: false,
      nextKey: '',
      selectedTheme: {
        themaGrpCd: '',
        themaNm: '',
        fluSig: '',
        fluRt: '',
        dtPrftRt: '',
      },
      dateTpOptions: [
        { value: '1', text: '1일' },
        { value: '5', text: '5일' },
        { value: '10', text: '10일' },
        { value: '20', text: '20일' },
        { value: '60', text: '60일' },
      ],
      stexTpOptions: [
        { value: '1', text: 'KRX' },
        { value: '2', text: 'NXT' },
        { value: '3', text: '통합' },
      ],
      fluPlAmtTpOptions: [
        { value: '1', text: '상위 기간수익률' },
        { value: '2', text: '하위 기간수익률' },
        { value: '3', text: '상위 등락률' },
        { value: '4', text: '하위 등락률' },
      ],
      fields: [
        { key: 'themaGrpCd', label: '테마코드' },
        { key: 'themaNm', label: '테마명' },
        { key: 'stkNum', label: '종목수' },
        { key: 'fluRt', label: '등락률' },
        { key: 'risingStkNum', label: '상승' },
        { key: 'fallStkNum', label: '하락' },
        { key: 'dtPrftRt', label: '기간수익률' },
        { key: 'mainStk', label: '주요종목' },
      ],
      detailFields: [
        { key: 'stkCd', label: '종목코드' },
        { key: 'stkNm', label: '종목명' },
        { key: 'curPrc', label: '현재가' },
        { key: 'predPre', label: '전일대비' },
        { key: 'fluRt', label: '등락률' },
        { key: 'accTrdeQty', label: '누적거래량' },
        { key: 'selBid', label: '매도호가' },
        { key: 'selReq', label: '매도잔량' },
        { key: 'buyBid', label: '매수호가' },
        { key: 'buyReq', label: '매수잔량' },
        { key: 'dtPrftRtN', label: '기간수익률' },
      ],
    }
  },
  created() {
    this.search()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async search() {
      this.loading = true
      this.errorMessage = ''
      this.hasMore = false
      this.nextKey = ''
      try {
        const data = await this.fetchThemeList('N', '')
        this.items = Array.isArray(data.items) ? data.items : []
        this.hasMore = !!data.hasMore
        this.nextKey = data.nextKey || ''
      } catch (error) {
        this.items = []
        this.errorMessage = error && error.message ? error.message : '테마 목록 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (!this.hasMore || !this.nextKey) return

      this.loadingMore = true
      this.errorMessage = ''
      try {
        const data = await this.fetchThemeList('Y', this.nextKey)
        const appended = Array.isArray(data.items) ? data.items : []
        this.items = this.items.concat(appended)
        this.hasMore = !!data.hasMore
        this.nextKey = data.nextKey || ''
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '추가 목록 조회에 실패했습니다.'
      } finally {
        this.loadingMore = false
      }
    },
    async fetchThemeList(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/theme/list', {
        dateTp: this.dateTp,
        stexTp: this.stexTp,
        themaNm: this.themaNm,
        fluPlAmtTp: this.fluPlAmtTp,
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    async openDetailModal(item) {
      if (!item || !item.themaGrpCd) return

      this.selectedTheme = {
        themaGrpCd: item.themaGrpCd,
        themaNm: item.themaNm || '',
        fluSig: item.fluSig || '',
        fluRt: item.fluRt || '',
        dtPrftRt: item.dtPrftRt || '',
      }
      this.detailItems = []
      this.detailErrorMessage = ''
      this.detailModalVisible = true
      this.detailLoading = true

      try {
        const response = await axios.post('/api/kiwoom/theme/detail', {
          dateTp: this.dateTp,
          stexTp: this.stexTp,
          themaGrpCd: item.themaGrpCd,
        })
        const data = this.parseApiResponse(response)
        this.selectedTheme = {
          themaGrpCd: data.themaGrpCd || item.themaGrpCd,
          themaNm: item.themaNm || '',
          fluSig: item.fluSig || '',
          fluRt: data.fluRt || item.fluRt || '',
          dtPrftRt: data.dtPrftRt || item.dtPrftRt || '',
        }
        this.detailItems = Array.isArray(data.items) ? data.items : []
      } catch (error) {
        this.detailErrorMessage = error && error.message ? error.message : '테마 상세 조회에 실패했습니다.'
      } finally {
        this.detailLoading = false
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(String(value).replace(/,/g, ''))
      if (Number.isNaN(parsed)) return value
      return parsed.toLocaleString()
    },
    formatSigned(value, suffix = '') {
      if (value === null || value === undefined || value === '') return '-'
      const text = String(value).trim()
      if (!text) return '-'
      if (suffix && text.includes(suffix)) return text
      return `${text}${suffix}`
    },
    rateClass(fluSig) {
      const value = String(fluSig || '').trim()
      if (value === '1' || value === '2') return 'text-danger'
      if (value === '4' || value === '5') return 'text-primary'
      return 'text-body'
    },
    rateClassByValue(value) {
      const parsed = Number(String(value || '').replace(/,/g, ''))
      if (Number.isNaN(parsed)) return 'text-body'
      if (parsed > 0) return 'text-danger'
      if (parsed < 0) return 'text-primary'
      return 'text-body'
    },
  },
}
</script>

<style scoped>
.theme-filter-row .form-label {
  font-size: 0.8rem;
  color: #6e6b7b;
}
</style>
